<template>
  <div class="sign-in">
    <div class="arrow-back-pass" v-show="showPassword">
      <a-button
        type="button"
        variant="none"
        aria-label="Back"
        @click="showPasswordModal(false)"
      >
        <img
          class="mr-1 clickable"
          :src="backIcon"
          alt=""
        />
      </a-button>
    </div>
    <username-selector
      ref="usernameSelector"
      :show-tabs="false"
      v-model="login"
      :type.once="login_type"
      title=""
      inputWidth="220px"
      @type-changed="onLoginTypeChanged"
      v-show="!showPassword"
      simplify
      @show-password="showPasswordModal"
    ></username-selector>
    <div class="password" v-if="passwordRequired">
      <a-form-input
        id="form"
        v-model.trim="password"
        :placeholder="$t('shared.placeholders.typePassword')"
        :aria-label="$t('shared.password')"
        :label="$t('shared.password')"
        :show-error="v$.password.$error"
        :error-message="$t('signUp.errors.invalidPassword')"
        required
        type="password"
        @keyup.enter="onSubmit"
        @input="onPasswordChange"
      />
      <slot name="forgot"></slot>
    </div>
    <slot name="continue" v-if="showSlot('continue')" :onClick="() => showPasswordModal(true)"></slot>
    <slot name="submit" v-if="showSlot('submit')" :onClick="() => onSubmit($event)"></slot>
    <slot name="sign-with-code" v-if="showSlot('sign-with-code')" :onClick="() => switchOTP(true)"></slot>
    <slot name="forgot-new-design"></slot>
    <slot name="switch-organization" v-if="showBackButton && !showPassword" :onClick="() => goToUrl()"></slot>
    <slot name="social" v-if="showSlot('socials')"></slot>
  </div>
</template>
<script>
import UsernameSelector from './AccountSettings/UsernameSelector.vue';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

export default {
  name: 'SignIn',
  components: {
    UsernameSelector,
  },
  setup() {
    const v$ = useVuelidate();
    return {
      v$,
    };
  },
  data() {
    return {
      login: '',
      login_type: 'email', // Default
      password: '',
      showPasswordValidation: false,
      showPassword: false,
      phoneNumber: null,
      clientId: null,
    };
  },
  validations() {
    return {
      password: { required },
    };
  },
  props: {
    /**
     * Why not use `emit` on the parent component?
     * Because the parent component is a Rails form,
     * and we need to set the value of the inputs
     * from this component.
     */
    inputLogin: {
      type: String,
      required: true,
    },
    inputPassword: {
      type: String,
      required: true,
    },
    authUserLogin: {
      type: String,
      required: false,
    },
    cancelOtpUrl: {
      type: String,
      required: false,
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
    backUrl: {
      type: String,
      required: false,
      default: '',
    },
    fusionCall: {
      type: Boolean,
      default: false,
    },
    mobileAppRequest: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    passwordRequired() {
      return this.login_type === 'email' && this.showPassword;
    },
    backIcon() {
      return new URL("../../assets/images/icons/arrow-left.svg", import.meta.url).href
    },
    isEdurooms() {
      return document.getElementById('is-edurooms')?.value === 'true';
    },
  },
  watch: {
    login: function(newVal) {
      const isPossibleEmail = /([a-zA-Z]|@)/.test(newVal);
      
      if (isPossibleEmail) {
        this.phoneNumber = { valid: false };
      } else {
        this.phoneNumber = parsePhoneNumber(newVal, { regionCode: 'US' });
      }
      
      this.setInputValue(this.inputLogin, newVal);
    },
    password: function(newVal) {
      this.setInputValue(this.inputPassword, newVal);
    },
  },
  mounted() {
    this.$analytics?.init();
    const schoolClientId = document.getElementById('school-client-id')?.value;
    this.clientId = schoolClientId !== 'false' ? schoolClientId : undefined;
    this.trackLoginSectionViewed();
    if(this.authUserLogin) {
      this.setInputValue(this.inputLogin, this.authUserLogin);
      this.switchOTP(true);
    }

  },
  methods: {
    // TODO - Check if referred_url is available un URL
    async trackLoginSectionViewed() {
      try {
        const referrer = document?.referrer;
        await this.$analytics?.track('login_section_viewed', {
          design_style: 'modern',
          auth_service: !this.fusionCall,
          referrer,
          client_id: this.clientId,
        });
      } catch (e) {
        // TODO: implement error handling
        console.warn('Error while tracking login section viewed event', e);
      }
    },
    async trackOTPRequest() {
      try {
          await this.$analytics?.track('login_otp_requested', {
            auth_service: !this.fusionCall,
            otp_channel: this.phoneNumber?.valid ? 'sms': 'email',
            client_id: this.clientId,
          });
        } catch (e) {
          // TODO: implement error handling      
          console.warn('Error while tracking login section viewed event', e);
        }
    },
    onLoginTypeChanged(type) {
      this.login_type = type;
      this.$emit('type-changed', type);
    },
    onPasswordChange(password) {
      this.password = password;
      this.$emit('password-changed', password);
    },
    showPasswordModal(value) {
      if (value) {
        this.$refs.usernameSelector.v$.$touch()
        if (this.$refs?.usernameSelector?.v$.$invalid) {
          return;
        }
      }
      this.showPassword = value;
    },
    setInputValue(id, value) {
      const input = document.querySelector(`#${id}`);
      input.value = value;
    },
    switchOTP(value) {
      if(value) this.trackOTPRequest();
      document.getElementById('user_sign_in_with_code')?.setAttribute('value', value);
      document.getElementById("new_user").submit();
    },
    goToUrl() {
      // NOTE (bug fix): the "Sign in to another organization" action must always
      // return to the district picker (backUrl). The mobile branch below routed to
      // cancelOtpUrl = destroy_user_session + param-less apptegy_oidc authorize,
      // which reset the session and dropped the StaffApp OIDC query params,
      // looping the user back to the same org (token never returned to the app).
      // backUrl goes to the picker host without resetting the session, so the OIDC
      // flow resumes via session[:after_otp_redirect_uri]. Kept commented (not
      // removed) temporarily for easier debugging.
      // if (this.mobileAppRequest) {
      //   window.location.href = this.cancelOtpUrl;
      //   return;
      // }
      if (this.backUrl) {
        window.location.href = this.backUrl;
        return;
      }

      const authServiceUrl = document.getElementById("auth-service-url")?.value;
      if (authServiceUrl) {
        window.location.href = authServiceUrl;
      }
    },
    showSlot(name) {
      switch(name) {
        case 'continue': return !this.showPassword && !this.phoneNumber?.valid && !this.fusionCall;
        case 'submit': return (this.showPassword || this.phoneNumber?.valid) && !this.fusionCall;
        case 'sign-with-code': return (this.showPassword && !this.phoneNumber?.valid);
        case 'socials': return !this.fusionCall;
        default: return false;
      }
    },
    onSubmit(){
      this.v$.$touch();
      if(this.v$.$invalid && !this.phoneNumber?.valid){
        return;
      }
      if (this.phoneNumber?.valid) {
       this.switchOTP(this.phoneNumber?.valid);
       return;
      }
      document.getElementById("new_user").submit();
    }
  },
};
</script>

<style lang="scss">
form.button_to {
  background-color: transparent !important;
}
form.button_to > button {
  appearance: none;
  border: 0;
  background-color: transparent !important;
  margin: 0;
  padding: 0;
  width: 100%;
}
.arrow-back-pass {
  position: relative;
  bottom: 30px;
  text-align: left;
}
div.password div.form-group {
  margin-bottom: 24px;
}
</style>
